"use client";

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import type { NavTarget, ResolvedApp, ResolvedScreen,
} from '@app/studio-runtime-web';
import StudioScreenRenderer from '@app/studio-runtime-web/renderer';

interface StudioClientShellProps {
  app: ResolvedApp;
  screen: ResolvedScreen;
  routeParams?: Record<string, string>;
  initialPathname?: string;
  /** Smarty-style variable bag for YAML `$bind: 'state.*'` paths. */
  initialState?: Record<string, unknown>;
  /**
   * The request locale the SERVER resolved from the original URL prefix.
   * When provided it wins over the pathname-derived locale: on rewritten
   * routes (e.g. /{lang}/learn served by internal filesystem paths) the
   * public prefix is absent from the pathname the renderer sees, and the
   * translation bundle / head sync / href localization must still follow the
   * requested language on both the server render and the first client render.
   */
  activeLocale?: string;
}

function normalizeRoutePath(input: string | undefined | null): string {
  const raw = String(input ?? '').trim();
  if (!raw) return '/';

  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withLeadingSlash.replace(/\/+$/, '') || '/';
}

function hasLocalePrefix(path: string, locale: string): boolean {
  return path === `/${locale}` || path.startsWith(`/${locale}/`);
}

function preserveUrlLocale(
  destination: string,
  currentPath: string,
  locales: readonly string[],
): string {
  if (locales.some((locale) => hasLocalePrefix(destination, locale))) return destination;

  const currentLocale = locales.find((locale) => hasLocalePrefix(currentPath, locale));
  return currentLocale
    ? `/${currentLocale}${destination === '/' ? '' : destination}`
    : destination;
}

/**
 * Rewrite the current path under the requested locale prefix, replacing any
 * existing supported prefix. Used when a flow selects a language in place
 * (e.g. `welcome.setLanguage`) and the host must re-render at the
 * locale-prefixed path so the server ships that locale's bundle.
 */
function localePrefixedPath(
  currentPath: string,
  locale: string,
  locales: readonly string[],
): string {
  const currentLocale = locales.find((candidate) =>
    hasLocalePrefix(currentPath, candidate),
  );
  const stripped = currentLocale
    ? currentPath.slice(`/${currentLocale}`.length) || '/'
    : currentPath;
  return `/${locale}${stripped === '/' ? '' : stripped}`;
}

/** Plausible BCP47-ish URL locale segment, mirroring the proxy's matcher. */
const PLAUSIBLE_LOCALE_SEGMENT_RE = /^[a-z]{2,3}(?:-[a-z]{2,4})?$/i;

/**
 * Derive the active locale from the SERVER-provided public pathname snapshot.
 *
 * The renderer otherwise has to re-derive the locale from browser-only state
 * (`usePathname()` / window.location). For rewritten Learn URLs those sources
 * can disagree with the public URL during SSR. Passing the locale resolved
 * from the same snapshot the server rendered with makes the translation-bundle
 * selection, head metadata sync, and hreflang set deterministic across the
 * SSR / hydration boundary.
 *
 * A prefix the app config does not list is still honored when a translation
 * bundle was loaded for it, so the active locale (and therefore the `$t`
 * bundle and head metadata) follows the URL language even when the app.yaml
 * i18n list and the loaded locale bundles have drifted apart.
 */
function resolveInitialLocale(
  path: string | undefined,
  locales: readonly string[],
  bundleLocales: readonly string[],
): string | undefined {
  if (!path) return undefined;
  const first = normalizeRoutePath(path).split('/').filter(Boolean)[0] ?? '';
  if (!first) return undefined;

  const configured = locales.find(
    (locale) => locale.toLowerCase() === first.toLowerCase(),
  );
  if (configured) return configured;

  const normalized = first.toLowerCase();
  if (
    PLAUSIBLE_LOCALE_SEGMENT_RE.test(normalized) &&
    bundleLocales.some((locale) => locale.toLowerCase() === normalized)
  ) {
    return normalized;
  }
  return undefined;
}

function scrollToTop(): void {
  const scrollRoot =
    document.getElementById('app-scroll-root') ?? document.scrollingElement;
  scrollRoot?.scrollTo(0, 0);
}

function bindScreenState(
  screen: ResolvedScreen,
  initialState?: Record<string, unknown>,
): ResolvedScreen {
  if (!initialState) return screen;
  return {
    ...screen,
    state: initialState,
    initialState,
  } as ResolvedScreen;
}

/**
 * True when `pathname` is the internal Next filesystem route that proxy.ts
 * rewrites public Learn URLs onto:
 * /learn
 * /learn/{categorySlug}
 * /learn/article/{explainer|how-to|overview}/{slug}
 *
 * Public URLs keep the locale prefix (`/en/learn`, `/en/explainer/{slug}`).
 * During SSR of a rewritten request, `usePathname()` returns this internal
 * path; after hydration it returns the public URL. Site chrome hrefs are
 * derived from the pathname, so that split is a hydration mismatch.
 */
function isInternalLearnFilesystemPath(input: string | null | undefined): boolean {
  const segments = normalizeRoutePath(input).split('/').filter(Boolean);
  if (segments[0] !== 'learn') return false;
  if (segments.length === 1) return true;
  if (segments.length === 2 && segments[1] !== 'article') return true;
  return (
    segments.length === 4 &&
    segments[1] === 'article' &&
    Boolean(segments[2]?.trim()) &&
    Boolean(segments[3]?.trim())
  );
}

/**
 * Prefer the server-provided public pathname when the live pathname is the
 * internal Learn rewrite target. Otherwise keep `usePathname()` so client
 * navigations update immediately.
 */
function resolveRendererPathname(
  livePathname: string | null,
  initialPathname: string | undefined,
): string | undefined {
  const live = livePathname ? normalizeRoutePath(livePathname) : undefined;
  const initial = initialPathname ? normalizeRoutePath(initialPathname) : undefined;

  if (
    live &&
    initial &&
    live !== initial &&
    isInternalLearnFilesystemPath(live) &&
    !isInternalLearnFilesystemPath(initial)
  ) {
    return initial;
  }

  return live ?? initial;
}

/** Map Studio navigation targets to Next.js routes. */
export default function StudioClientShell({
  app,
  screen,
  routeParams,
  initialPathname,
  initialState,
  activeLocale: activeLocaleProp,
}: StudioClientShellProps) {
  const router = useRouter();
  const pathname = usePathname();

  /**
   * The first client render IS the hydration render, and it must reproduce
   * the server HTML exactly. The server rendered with the public pathname
   * snapshot it was given (`initialPathname`) — for rewritten Learn URLs the
   * live `usePathname()` can be the internal filesystem target, which derives
   * different site-chrome hrefs (e.g. `/welcome` vs `/en/welcome`) and fails
   * hydration.
   *
   * So the client does NOT re-derive the render pathname for the initial
   * render: it reuses the server snapshot verbatim. The live pathname is
   * adopted only after mount, when the shell updates outside of hydration
   * and client navigations can safely re-derive from `usePathname()`.
   */
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
  }, []);
  const rendererPathname = isMountedRef.current
    ? resolveRendererPathname(pathname, initialPathname)
    : initialPathname
      ? normalizeRoutePath(initialPathname)
      : pathname;

  // The active locale: the host-resolved request locale (server snapshot)
  // wins; otherwise it is derived from the same server snapshot used for the
  // initial render (see resolveInitialLocale).
  const activeLocale = useMemo(
    () =>
      (typeof activeLocaleProp === 'string' && activeLocaleProp.trim()
        ? activeLocaleProp.trim()
        : undefined) ??
      resolveInitialLocale(
        initialPathname,
        app.i18n?.locales ?? [],
        Object.keys(app.translationsByLocale ?? {}),
      ),
    [
      activeLocaleProp,
      initialPathname,
      app.i18n?.locales,
      app.translationsByLocale,
    ],
  );

  const screenWithState = useMemo(
    () => bindScreenState(screen, initialState),
    [initialState, screen],
  );

  // Set by handleNavigate and consumed by the effect below, so the shell
  // scrolls to the top only for navigations it initiated. Browser
  // back/forward (router.back / popstate) keep their native scroll
  // restoration.
  const pendingScrollRef = useRef(false);

  // Next.js owns the URL and the rendered tree for navigations initiated
  // through its router. Do NOT also write the URL with history.pushState /
  // history.replaceState here: those manual entries clone the current
  // history state, which carries the previously rendered page tree, so the
  // App Router treats the new entry as already rendered and never re-runs
  // the catch-all server page — the URL changed but the screen content
  // stayed stale.
  useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    scrollToTop();
  }, [pathname]);

  const handleNavigate = useCallback(
    (target: NavTarget) => {
      if (target.kind === 'external' && target.url) {
        window.location.assign(target.url);
        return;
      }

      if (target.kind === 'back') {
        router.back();
        return;
      }

      let dest: string | null = null;

      if (target.kind === 'screen' && target.to) {
        const route = app.config.routes?.[target.to];
        dest = route ?? `/${target.to}`;
      } else if (target.kind === 'route' && target.to) {
        dest = target.to;
      }

      if (!dest) return;

      const normalizedCurrent = normalizeRoutePath(
        resolveRendererPathname(pathname, initialPathname) ?? pathname ?? initialPathname,
      );
      const normalizedDest = preserveUrlLocale(
        normalizeRoutePath(dest),
        normalizedCurrent,
        app.i18n?.locales ?? [],
      );

      if (normalizedCurrent === normalizedDest) return;

      pendingScrollRef.current = true;
      if (target.replace) {
        router.replace(normalizedDest, { scroll: false });
      } else {
        router.push(normalizedDest, { scroll: false });
      }
    },
    [app.config.routes, app.i18n?.locales, initialPathname, pathname, router],
  );

  /**
   * Switch to the requested locale with a SOFT navigation.
   *
   * The renderer calls this when a flow changes `state.language`. Replacing
   * the URL through Next's router lets the catch-all server page build the
   * locale-specific client model without reloading the document.
   */
  const handleActiveLocaleChange = useCallback(
    (locale: string) => {
      if (typeof window === 'undefined') return;
      const locales = app.i18n?.locales ?? [];
      if (locales.length > 0 && !locales.includes(locale)) return;

      const actualCurrent = normalizeRoutePath(window.location.pathname);
      const dest = localePrefixedPath(actualCurrent, locale, locales);
      if (dest === actualCurrent) return;

      // Soft navigation fetches the server model for the locale-prefixed URL.
      // Keep the current scroll position for this in-place language switch.
      router.replace(dest, { scroll: false });
    },
    [app.i18n?.locales, router],
  );

  const rendererProps = {
    app,
    screen: screenWithState,
    navigate: handleNavigate,
    routeParams,
    initialPathname: rendererPathname,
    currentPathname: rendererPathname,
    activeLocale,
    onActiveLocaleChange: handleActiveLocaleChange,
    ...(initialState ? { initialState } : {}),
  };

  return <StudioScreenRenderer {...rendererProps} />;
}